import { Button } from '@peacecover/component';
import { ArrowLeft, MoreSmall } from '@peacecover/icon';
import { useGetCastRelationships } from '../../domains/scene/ui/hooks/useGetCastRelationships';
import { useHandleHistoryBack } from '../../utils/useHandleHistoryBack';
import PreviousCastRelationshipList from './components/PreviousCastRelationshipList';
import GradientLoadingSpinner from '../components/GradientLoadingSpinner';
import type { components } from '../../types/schema';

type CastRelationship = components['schemas']['ApiV1CastRelationship'];

const CastRelationshipsPage = () => {
  const { data: castRelationships, isLoading } = useGetCastRelationships({ page: 1, perPage: 10 });

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background-dark">
        <GradientLoadingSpinner size={36} />
      </div>
    );
  }

  const currentCastRelationship = castRelationships?.[0];

  return (
    <div className="min-h-screen pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] bg-background-dark">
      <Header />
      <div className="px-[24px] py-[10px] mt-[10px] flex flex-col gap-[14px] items-start">
        <h1 className="text-[26px] font-bold leading-[30px] text-main-dark-1">
          새로운 조합으로
          <br />
          이야기를 감상할까요?
        </h1>
        <p className="text-[15px] font-medium leading-[18px] text-main-dark-4">
          이전에 봤던 조합으로 다시 변경도 가능해요
        </p>
      </div>
      <CurrentCastRelationshipSection
        currentCastRelationship={currentCastRelationship ?? undefined}
      />
      {castRelationships?.length && castRelationships?.length > 1 ? (
        <PreviousCastRelationshipList castRelationships={castRelationships?.slice(1)} />
      ) : (
        <EmptyCase />
      )}
    </div>
  );
};

export default CastRelationshipsPage;

const Header = () => {
  const handleHistoryBack = useHandleHistoryBack();
  return (
    <header className="flex items-center justify-between px-[16px] py-[8px]">
      <button type="button" onClick={handleHistoryBack}>
        <ArrowLeft size={24} className="text-main-dark-1" />
      </button>
      <p className="text-[17px] font-bold leading-[20px] text-main-dark-1">인물 조합 변경하기</p>
      <MoreSmall size={24} className="text-main-dark-1" />
    </header>
  );
};

const CurrentCastRelationshipSection = ({
  currentCastRelationship
}: {
  currentCastRelationship: CastRelationship | undefined;
}) => {
  return (
    <div className="mt-[33px] flex items-center gap-[11px] justify-center">
      <div className="flex flex-col items-center gap-[12px]">
        <div className="relative w-[88px] h-[88px]">
          {currentCastRelationship?.personAThumbnailUrl && (
            <img
              src={currentCastRelationship?.personAThumbnailUrl}
              alt="personAThumbnailUrl"
              className="rounded-full object-cover"
            />
          )}
          <img
            src="/assets/icons/profile_bubble.png"
            alt="profile_bubble"
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          />
        </div>
        <span className="text-[12px] font-medium leading-[14px] text-main-dark-1">
          {currentCastRelationship?.personAName}
        </span>
      </div>
      <div className="flex flex-col items-center gap-[12px]">
        <DoubleArrow />
        <div className="px-[8px] py-[4px] bg-[linear-gradient(47deg,rgba(69,220,174,0.30)_11.79%,rgba(93,62,250,0.30)_99.08%)] rounded-[30px] text-[11px] font-semibold leading-[13px]">
          <span className="bg-[linear-gradient(47deg,#45DCAE_0%,#5D3EFA_99.08%)] bg-clip-text text-transparent">
            보는중
          </span>
        </div>
      </div>
      <div className="flex flex-col items-center gap-[12px]">
        <div className="relative w-[88px] h-[88px]">
          {currentCastRelationship?.personBThumbnailUrl && (
            <img
              src={currentCastRelationship?.personBThumbnailUrl}
              alt="personBThumbnailUrl"
              className="rounded-full object-cover"
            />
          )}
          <img
            src="/assets/icons/profile_bubble.png"
            alt="profile_bubble"
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          />
        </div>
        <span className="text-[12px] font-medium leading-[14px] text-main-dark-1">
          {currentCastRelationship?.personBName}
        </span>
      </div>
    </div>
  );
};

const EmptyCase = () => {
  return (
    <div className="mt-[72px] flex flex-col items-center px-[24px] w-full">
      <div className="flex flex-col items-center gap-[16px]">
        <p className="text-[50px]">😳</p>
        <p className="text-[17px] font-bold leading-[20px] text-main-dark-2">
          이전에 사용한 인물 조합이 없어요
        </p>
      </div>
      <div className="fixed bottom-0 left-0 right-0 px-[24px] pb-[calc(env(safe-area-inset-bottom)+36px)]">
        <Button size="large" className="w-full text-[17px] font-semibold mt-[192px]">
          새로운 조합으로 변경하기
        </Button>
      </div>
    </div>
  );
};

const DoubleArrow = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="73"
      height="8"
      viewBox="0 0 73 8"
      fill="none"
      aria-label="double-arrow"
      role="img"
      className="mt-[20px]"
    >
      <path
        d="M0.146447 3.32809C-0.0488155 3.52335 -0.0488155 3.83993 0.146447 4.03519L3.32843 7.21717C3.52369 7.41244 3.84027 7.41244 4.03553 7.21717C4.2308 7.02191 4.2308 6.70533 4.03553 6.51007L1.20711 3.68164L4.03553 0.853214C4.2308 0.657951 4.2308 0.341369 4.03553 0.146107C3.84027 -0.0491555 3.52369 -0.0491555 3.32843 0.146107L0.146447 3.32809ZM72.8536 4.03519C73.0488 3.83993 73.0488 3.52335 72.8536 3.32809L69.6716 0.146107C69.4763 -0.0491555 69.1597 -0.0491555 68.9645 0.146107C68.7692 0.341369 68.7692 0.657951 68.9645 0.853214L71.7929 3.68164L68.9645 6.51007C68.7692 6.70533 68.7692 7.02191 68.9645 7.21717C69.1597 7.41244 69.4763 7.41244 69.6716 7.21717L72.8536 4.03519ZM0.5 3.68164V4.18164H72.5V3.68164V3.18164H0.5V3.68164Z"
        fill="#D6D6D6"
      />
    </svg>
  );
};
